import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { TicketCheck, Plus, Loader2, MessageCircle } from 'lucide-react';
import API_URL from '../../config/api';

// ⭐ Mismo número comercial de WhatsApp que ya usa marketos-landing
// (marketos-landing/src/lib/whatsapp.js) — soporte directo vía WhatsApp
// para planes Estándar en adelante ($25+), en vez de solo tickets escritos.
const WHATSAPP_SUPPORT_NUMBER = '593997125285';
const PLAN_RANK = { basico: 0, estandar: 1, premium: 2, empresarial: 3 };

const TICKET_TYPES = [
  { value: 'bug', label: '🐛 Reporte de error / Bug' },
  { value: 'request', label: '✨ Solicitud de mejora' },
  { value: 'question', label: '❓ Pregunta / Consulta' },
  { value: 'other', label: '📌 Otro' }
];

const TICKET_PRIORITIES = [
  { value: 'low', label: '⬇️ Baja — no es urgente' },
  { value: 'medium', label: '🔵 Media — puede esperar' },
  { value: 'high', label: '⚡ Alta — afecta mi trabajo' },
  { value: 'urgent', label: '🚨 Urgente — el sistema no funciona' }
];

const STATUS_STYLES = {
  open:        { bg: 'bg-red-100',    text: 'text-red-700',    label: '🔴 Abierto' },
  in_progress: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: '🟡 En proceso' },
  resolved:    { bg: 'bg-green-100',  text: 'text-green-700',  label: '🟢 Resuelto' },
  closed:      { bg: 'bg-gray-100',   text: 'text-gray-600',   label: '⚫ Cerrado' }
};

export default function SoporteTickets({ clientId, token, plan, businessName }) {
  const hasWhatsappSupport = (PLAN_RANK[plan] ?? 0) >= PLAN_RANK.estandar;
  const whatsappLink = `https://wa.me/${WHATSAPP_SUPPORT_NUMBER}?text=${encodeURIComponent(
    `Hola, soy ${businessName || 'un cliente'} de Gestor SaaS POS y necesito soporte.`
  )}`;
  const [tickets, setTickets] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    subject: '',
    description: '',
    type: 'bug',
    priority: 'medium'
  });

  useEffect(() => {
    loadMyTickets();
  }, []);

  const loadMyTickets = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/pos/tickets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTickets(res.data);
    } catch (e) {
      console.error('Error al cargar tickets:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.description.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/pos/tickets`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess(true);
      setForm({ subject: '', description: '', type: 'bug', priority: 'medium' });
      setShowForm(false);
      loadMyTickets();
      setTimeout(() => setSuccess(false), 4000);
    } catch (e) {
      alert(e.response?.data?.error || 'Error al enviar el ticket');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <TicketCheck className="w-6 h-6 text-purple-600" />
            Soporte Técnico
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Reporta errores o solicita mejoras. Te responderemos a la brevedad.
          </p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-semibold text-sm shadow"
        >
          <Plus className="w-4 h-4" />
          Nuevo ticket
        </button>
      </div>

      {/* Soporte directo por WhatsApp (Estándar en adelante) */}
      {hasWhatsappSupport && (
        <a
          href={whatsappLink}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-between gap-4 mb-6 p-4 bg-green-50 border-2 border-green-200 rounded-xl hover:bg-green-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="bg-green-500 p-2.5 rounded-full shrink-0">
              <MessageCircle className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-bold text-green-900 text-sm">Soporte directo por WhatsApp</p>
              <p className="text-xs text-green-700">Tu plan incluye atención prioritaria por chat. Toca para escribirnos ahora.</p>
            </div>
          </div>
          <span className="text-xs font-bold text-green-700 shrink-0">Abrir WhatsApp →</span>
        </a>
      )}

      {/* Success banner */}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm font-medium">
          ✅ Tu ticket fue enviado exitosamente. Te responderemos pronto.
        </div>
      )}

      {/* Create form */}
      {showForm && (
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border border-purple-100">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Crear nuevo ticket</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Asunto *</label>
              <input
                type="text"
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                placeholder="Describe brevemente el problema"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descripción detallada *</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm resize-none"
                placeholder="Describe qué pasó, cuándo ocurrió y qué pasos hiciste antes del problema..."
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                <select
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                >
                  {TICKET_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prioridad</label>
                <select
                  value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                >
                  {TICKET_PRIORITIES.map(p => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setShowForm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm">
                Cancelar
              </button>
              <button type="submit" disabled={sending}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60">
                {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> Enviando...</> : 'Enviar ticket'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* My tickets list */}
      <div>
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
          Mis tickets ({tickets.length})
        </h3>

        {loading ? (
          <div className="text-center py-8 text-gray-400">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
            <p className="text-sm">Cargando tickets...</p>
          </div>
        ) : tickets.length === 0 ? (
          <div className="bg-white rounded-xl shadow p-10 text-center text-gray-400">
            <TicketCheck className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="font-medium text-gray-500">No has creado ningún ticket aún</p>
            <p className="text-sm mt-1">Si tienes algún problema o sugerencia, ¡crea tu primer ticket!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {tickets.map(ticket => {
              const st = STATUS_STYLES[ticket.status] || STATUS_STYLES.open;
              return (
                <div key={ticket.id} className="bg-white rounded-xl shadow p-4 border border-gray-100">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${st.bg} ${st.text}`}>
                          {st.label}
                        </span>
                        <span className="text-xs text-gray-400">
                          {TICKET_TYPES.find(t => t.value === ticket.type)?.label || ticket.type}
                        </span>
                      </div>
                      <p className="font-semibold text-gray-900 text-sm truncate">{ticket.subject}</p>
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{ticket.description}</p>

                      {ticket.admin_notes && (
                        <div className="mt-2 p-2.5 bg-purple-50 border border-purple-100 rounded-lg">
                          <p className="text-xs font-semibold text-purple-700 mb-0.5">💬 Respuesta del equipo:</p>
                          <p className="text-xs text-purple-800">{ticket.admin_notes}</p>
                        </div>
                      )}

                      <p className="text-xs text-gray-400 mt-2">
                        📅 {new Date(ticket.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}
                        {ticket.updated_at && ticket.updated_at !== ticket.created_at && (
                          <span className="ml-2">• Actualizado: {new Date(ticket.updated_at).toLocaleDateString('es-ES')}</span>
                        )}
                      </p>
                    </div>
                    <span className="text-xs text-gray-300 font-mono shrink-0">#{ticket.id}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
